import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useNavigation } from '@/hooks/useNavigation'
import { useCallback, useEffect, useMemo } from 'react'
import {
  APPOINTMENT_STATES_ENUM,
  AppointmentRoutine,
  AppointmentRoutineProps,
  UseMonitoringProps,
} from '../types'
import {
  ModalSettings,
  useModalContext,
} from '@/components/Modal/contexts/ModalContext'
import { getCache } from '@/utils/cache'
import { AUTHENTICATED_2FA_TIME } from '@/modules/Auth/pages/TwoFactorAuth/constants'
import { LOGGED_IN_WITH_MAGIC_LINK } from '@/modules/Auth/pages/MagicLink/consts'
import { useCallMonitoringStore } from '@/modules/VideoCall/store/call-monitoring-store'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { callAppointmentMonitoring } from '@/modules/VideoCall/video-call-services'
import { verifyTokenStatus } from '@/modules/RealtimeAttendance/services'
import { useFinishMeet } from '@conexasaude/vonage-sdk'
import { roomPaths } from '@/modules/Attendance/submodules/Room/constants'
import { DateTime } from 'luxon'

export const useMonitoringScheduled = ({
  realtimeAppointments,
}: UseMonitoringProps) => {
  const { clinic } = useAuthContext()
  const location = useLocation()
  const navigate = useNavigation()
  const { showModal } = useModalContext()
  const { realtimeAttendance } = useRealtimeAttendanceContext()
  const { embed } = useThemeGeneratorContext()
  const { destroyMeet } = useFinishMeet()
  const embedOrEmbedFull = embed?.value || embed?.full

  const waitingRoomScheduleMatch = useRouteMatch([
    '/atendimento-agendado/sala-de-espera/:id',
    roomPaths.scheduled.fullPath,
    roomPaths.immediate.fullPath,
  ])

  const isOnVideoCallPage = ['chamada', roomPaths.basePath].some(path =>
    location.pathname.includes(path),
  )

  const isOnRatingPage = location.pathname.includes('avaliacao')

  const showModalValidating2FA = useCallback(
    (id: string, modalSettings: ModalSettings) => {
      const needTwoFactorAuth =
        clinic?.flag?.HabilitarAutenticacaoDoisFatoresLoginPaciente &&
        !getCache(AUTHENTICATED_2FA_TIME) &&
        !getCache(LOGGED_IN_WITH_MAGIC_LINK)

      if (!needTwoFactorAuth) showModal(id, modalSettings)
    },
    [clinic?.flag?.HabilitarAutenticacaoDoisFatoresLoginPaciente, showModal],
  )

  const isScheduledDateToday =
    realtimeAttendance?.nextScheduled?.scheduledDate?.split(' ')[0] ===
    DateTime.now().toFormat('dd/MM/yyyy')

  const scheduledRoutine: AppointmentRoutine = useMemo(() => {
    const appointmentStarted = (props: AppointmentRoutineProps) => {
      useCallMonitoringStore.setState({
        appointmentId: props?.appointmentId,
        roomId: props?.roomId,
      })

      if (waitingRoomScheduleMatch?.isExact) return

      if (isScheduledDateToday) {
        setTimeout(() => {
          showModalValidating2FA('ATTENDANCE_STARTED', {
            title: 'Falta pouco!',
          })
        }, 1000)
      }
    }

    return {
      [APPOINTMENT_STATES_ENUM.CALL_STARTED](props) {
        if (isOnVideoCallPage) return

        callAppointmentMonitoring(props?.appointmentId).then(response => {
          useCallMonitoringStore.setState({
            call: response,
            appointmentId: props?.appointmentId,
          })

          if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
            verifyTokenStatus(Number(props?.appointmentId)).then(
              tokenIsValidated => {
                if (tokenIsValidated) {
                  showModalValidating2FA('SCHEDULED_CALL_STARTED', {
                    title: 'Está na hora da sua consulta',
                    lockable: true,
                    persistent: true,
                    width: 512,
                  })
                } else {
                  showModal('CALL_STARTED_WITH_TOKEN', {
                    title: 'Informe o token de segurança',
                    persistent: true,
                  })
                }
              },
            )
          } else {
            showModalValidating2FA('SCHEDULED_CALL_STARTED', {
              title: 'Está na hora da sua consulta',
              lockable: true,
              persistent: true,
              width: 512,
            })
          }
        })
      },
      [APPOINTMENT_STATES_ENUM.CALL_RESTARTED](props) {
        if (isOnVideoCallPage) return
        callAppointmentMonitoring(props?.appointmentId).then(response => {
          useCallMonitoringStore.setState({
            appointmentId: props?.appointmentId,
            call: response,
          })

          showModalValidating2FA('CALL_RESTARTED', {
            title: 'Consulta Reiniciada',
            persistent: true,
            width: 512,
          })
        })
      },
      [APPOINTMENT_STATES_ENUM.ATTENDANCE_STARTED](props) {
        if (isOnVideoCallPage) return

        return appointmentStarted(props)
      },
      [APPOINTMENT_STATES_ENUM.ATTENDANCE_STARTING](props) {
        if (isOnVideoCallPage) return

        return appointmentStarted(props)
      },
      [APPOINTMENT_STATES_ENUM.CALL_ENDED](props) {
        const isCurrentAppointment =
          props?.appointmentId ===
          useCallMonitoringStore.getState().appointmentId

        if (waitingRoomScheduleMatch?.isExact && isCurrentAppointment) {
          showModal('CANCELLED_BY_MEDIC', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }

        if (!isOnVideoCallPage || !isCurrentAppointment) return

        destroyMeet()

        if (clinic?.flag?.DesabilitarNpsTelemedicina) {
          embedOrEmbedFull ? navigate('/meus-atendimentos') : navigate('/')
          return
        }

        setTimeout(() => {
          navigate(`/avaliacao/${props?.appointmentId}`)

          useCallMonitoringStore.setState({
            appointmentId: null,
            call: null,
          })
        }, 300)
      },
    }
  }, [
    waitingRoomScheduleMatch?.isExact,
    isScheduledDateToday,
    showModalValidating2FA,
    isOnVideoCallPage,
    clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno,
    clinic?.flag?.DesabilitarNpsTelemedicina,
    showModal,
    destroyMeet,
    embedOrEmbedFull,
    navigate,
  ])

  useEffect(() => {
    if (
      realtimeAttendance?.call ||
      realtimeAttendance?.nextScheduled ||
      isOnRatingPage ||
      !waitingRoomScheduleMatch
    )
      return

    if (realtimeAppointments) {
      const filteredScheduled = realtimeAppointments?.filter(
        status => status.modoAtendimento === 'AGENDADO',
      )

      filteredScheduled?.forEach(appointment => {
        if (scheduledRoutine[appointment.status]) {
          scheduledRoutine[appointment.status]({
            appointmentId: appointment.idAtendimento,
            roomId: appointment.roomId,
          })
        }
      })
    }
  }, [
    isOnRatingPage,
    realtimeAppointments,
    scheduledRoutine,
    realtimeAttendance?.call,
    realtimeAttendance?.nextScheduled,
    waitingRoomScheduleMatch,
  ])
}
